import { create } from 'zustand';

import type { CartStore } from './types';

const defaultCartState = {
  mainArticleCount: 0,
  isBusy: false,
  pendingArticle: null,
  cartLiveValues: undefined,
  deletedArticleIds: [],
  articleQuantities: {}
};

/**
 * Cart state store
 *
 * Manages:
 * - mainArticleCount: Badge count for cart icon
 * - isBusy: Mutation busy state for cart operations
 *
 * Usage:
 * ```typescript
 * const { mainArticleCount, isBusy } = useCartStore();
 * const setCartBusy = useCartStore((state) => state.setCartBusy);
 * ```
 */
export const useCartStore = create<CartStore>(set => ({
  ...defaultCartState,
  setMainArticleCount: count => set({ mainArticleCount: count }),
  setCartBusy: busy => set({ isBusy: busy }),
  setArticlePending: pending => set({ pendingArticle: pending }),
  startQuantityMutation: pending => set({ isBusy: true, pendingArticle: pending }),
  endQuantityMutation: () => set({ isBusy: false, pendingArticle: null }),
  setCartLiveValues: values => set({ cartLiveValues: values }),
  setArticleQuantity: (articleId, quantity) =>
    set(state => ({ articleQuantities: { ...state.articleQuantities, [articleId]: quantity } })),
  addDeletedArticleId: id =>
    set(state => ({
      deletedArticleIds: state.deletedArticleIds.includes(id)
        ? state.deletedArticleIds
        : [...state.deletedArticleIds, id]
    })),
  resetCart: () => set(defaultCartState)
}));
